import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { HF, LockIcon, cleanName, localChainName } from "./homeFeedUi";
import ProductTileImage from "./ProductTileImage";
import { EXAMPLE_PREVIEW_ZIP } from "./homeFeedLogic";
import { CLEARANCE_CHAINS, bandBadge, publicDealPreviewQuery, publicFreeDealQuery, type ClearanceChain } from "../../services/publicClearanceQueries";
import { getProductImageUrl } from "../../utils/imageUtils";
import { track } from "../../utils/analytics";


/**
 * "/clearance" for guests and free accounts: one store at a time (each store is two
 * public calls against a budget of twelve per ten minutes), its nearby finds with the
 * price locked, and a bar to unlock them (sign up, or upgrade on a free account).
 */
export default function ClearanceView({
  zip,
  onSubmitZip,
  unlockUrl,
  onUnlock,
  freeMember,
}: {
  zip: string;
  onSubmitZip: (zip: string) => void;
  unlockUrl: (source: string) => string;
  onUnlock: (trigger: string) => void;
  freeMember: boolean;
}) {
  const [chain, setChain] = useState<ClearanceChain>("walmart");
  const [editingZip, setEditingZip] = useState(false);
  const [zipInput, setZipInput] = useState("");
  const [zipError, setZipError] = useState("");
  const hasZip = /^\d{5}$/.test(zip);
  // Real clearance from another area, labelled as examples, until the visitor adds a ZIP.
  const lookupZip = hasZip ? zip : EXAMPLE_PREVIEW_ZIP;
  const count = useQuery({ ...publicFreeDealQuery(lookupZip, chain), enabled: hasZip });
  const preview = useQuery(publicDealPreviewQuery(lookupZip, chain));
  const finds = (preview.data?.deals || []).filter((deal) => deal.retailer === chain && deal.image);
  const store = localChainName(chain);
  const total = count.data?.totalDeals;
  const showZipForm = !hasZip || editingZip;
  const cardUrl = unlockUrl("clearance_card");

  const submitZip = (event: FormEvent) => {
    event.preventDefault();
    const value = zipInput.trim();
    if (!/^\d{5}$/.test(value)) {
      setZipError("Enter a 5-digit ZIP code");
      return;
    }
    setZipError("");
    setEditingZip(false);
    onSubmitZip(value);
    track("guest_zip_entered", { surface: "web", retailer: "local", trigger: "clearance_page", source: "clearance" });
  };
  const pickChain = (next: ClearanceChain) => {
    if (next === chain) return;
    track("guest_retailer_selected", { surface: "web", retailer: next, trigger: "clearance_chip", source: "clearance" });
    setChain(next);
  };

  return (
    <div className="mx-auto max-w-[1100px] pb-32 sm:pb-36" data-testid="clearance-page">
      <div className="flex items-center justify-between gap-3 px-1">
        <h1 className="m-0 text-[28px] font-extrabold tracking-[-0.8px] sm:text-[34px] sm:tracking-[-1px]">Clearance</h1>
        {hasZip && (
          <button
            type="button"
            onClick={() => {
              setZipInput(zip);
              setEditingZip((open) => !open);
            }}
            aria-label={`Near ${zip}. Change ZIP code`}
            aria-expanded={editingZip}
            className="flex h-9 flex-shrink-0 items-center gap-1.5 rounded-full pl-3 pr-3.5 text-[14px] font-semibold transition-colors hover:bg-[var(--hf-hover-strong)]"
            style={{ background: HF.surface2, color: HF.ink }}
          >
            <PinIcon />
            {zip}
          </button>
        )}
      </div>

      {showZipForm && (
        <form onSubmit={submitZip} className="mt-4 flex max-w-[460px] flex-col gap-3 px-1">
          {!hasZip && <p className="m-0 text-[20px] font-extrabold leading-[25px] tracking-[-0.5px]">What’s on clearance near you?</p>}
          <label className="flex h-14 items-center rounded-full border pl-5 pr-[5px] focus-within:ring-2 focus-within:ring-[#FFD24A]" style={{ borderColor: HF.border, background: HF.surface2 }}>
            <span className="sr-only">ZIP code</span>
            <input
              value={zipInput}
              onChange={(event) => {
                setZipInput(event.target.value.replace(/\D/g, "").slice(0, 5));
                if (zipError) setZipError("");
              }}
              inputMode="numeric"
              autoComplete="postal-code"
              placeholder="ZIP code"
              maxLength={5}
              className="min-w-0 flex-1 border-0 bg-transparent text-[18px] font-semibold tracking-[1px] outline-none placeholder:font-normal placeholder:tracking-normal placeholder:text-[var(--hf-placeholder)]"
              style={{ color: HF.ink }}
            />
            <button type="submit" aria-label="Show deals" className="flex h-[46px] w-[46px] flex-shrink-0 items-center justify-center rounded-full hover:opacity-90" style={{ background: HF.inkBtn, color: HF.onInkBtn }}>
              <ArrowIcon />
            </button>
          </label>
          {zipError && <p className="m-0 pl-5 text-[13px]" style={{ color: HF.red }} role="alert">{zipError}</p>}
        </form>
      )}

      <div role="tablist" aria-label="Stores" className="scrollbar-hide -mx-3 mt-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:px-1">
        {CLEARANCE_CHAINS.map((id) => {
          const active = id === chain;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => pickChain(id)}
              className="flex h-9 flex-shrink-0 items-center whitespace-nowrap rounded-full px-4 text-[14px] font-semibold transition-colors"
              style={active ? { background: HF.inkBtn, color: HF.onInkBtn } : { background: HF.surface2, color: HF.body }}
            >
              {localChainName(id)}
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex min-h-[36px] items-baseline gap-2 px-1" aria-live="polite">
        {!hasZip ? (
          <span className="text-[15px]" style={{ color: HF.muted }}>Examples from {store} in another area</span>
        ) : typeof total === "number" && total > 0 ? (
          <>
            <span className="text-[30px] font-extrabold tabular-nums tracking-[-1px]">{total.toLocaleString()}</span>
            <span className="text-[15px]" style={{ color: HF.muted }}>markdowns at {store} near you</span>
          </>
        ) : count.isLoading ? (
          <span className="block h-[22px] w-[180px] self-center rounded-lg" style={{ background: HF.skeleton }} />
        ) : (
          <span className="text-[15px]" style={{ color: HF.muted }}>No markdowns at {store} near you yet today</span>
        )}
      </div>

      <div className="mt-3.5 grid grid-cols-2 gap-x-3 gap-y-5 px-1 sm:grid-cols-3 sm:gap-x-4 lg:grid-cols-4">
        {preview.isLoading
          ? [0, 1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col gap-2">
                <span className="block aspect-square rounded-[20px]" style={{ background: HF.skeleton }} />
                <span className="block h-3.5 w-[45%] rounded-md" style={{ background: HF.skeleton }} />
                <span className="block h-3.5 w-[85%] rounded-md" style={{ background: HF.skeleton }} />
              </div>
            ))
          : finds.map((deal, index) => (
              <Link
                key={`${deal.name}-${index}`}
                to={cardUrl}
                onClick={() => onUnlock("clearance_card")}
                className="group flex flex-col gap-1 no-underline"
                style={{ color: HF.ink }}
                aria-label={`${cleanName(deal.name)}, ${deal.discountBand} at ${store}. Price and aisle for members`}
              >
                <span className="relative flex aspect-square items-center justify-center overflow-hidden rounded-[20px]" style={{ background: HF.tile }}>
                  <ProductTileImage src={getProductImageUrl(deal.image, "300")} className="h-[74%] w-[74%] object-contain" />
                  <span className="absolute left-2.5 top-2.5 flex h-6 items-center rounded-full px-2 text-[12.5px] font-extrabold" style={{ background: "#0B0B0D", color: "#FF6B72" }}>
                    {bandBadge(deal.discountBand)}
                  </span>
                </span>
                <span className="mt-1.5 flex items-center gap-1.5" style={{ color: HF.muted }}>
                  <span className="block h-3.5 w-[54px] rounded-[7px]" style={{ background: HF.skeleton }} aria-hidden="true" />
                  <LockIcon size={12} />
                </span>
                <span className="line-clamp-2 text-[13px] leading-[17px]" style={{ color: HF.body }}>{cleanName(deal.name)}</span>
                <span className="text-[12px]" style={{ color: HF.muted }}>{store}</span>
              </Link>
            ))}
      </div>

      <div className="fixed inset-x-3 bottom-3 z-30 mx-auto flex h-[62px] max-w-[560px] items-center gap-3 rounded-full border pl-5 pr-2 shadow-[0_8px_24px_var(--hf-shadow)] sm:bottom-6" style={{ background: HF.surface, borderColor: HF.border }}>
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-bold">Prices &amp; aisles</span>
          <span className="block truncate text-[12px]" style={{ color: HF.muted }}>{freeMember ? "Included with any plan" : "Free account required"}</span>
        </span>
        <Link
          to={unlockUrl("clearance_bar")}
          onClick={() => onUnlock("clearance_bar")}
          className="flex h-[46px] flex-shrink-0 items-center rounded-full px-6 text-[15px] font-bold no-underline hover:opacity-90"
          style={{ background: HF.inkBtn, color: HF.onInkBtn }}
          data-testid="clearance-unlock"
        >
          {freeMember ? "Upgrade" : "Sign up"}
        </Link>
      </div>
    </div>
  );
}

function PinIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFD24A" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

export function ArrowIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
